import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import Sidebar from './components/Sidebar';
import TaskList from './components/TaskList';
import TaskForm from './components/TaskForm';
import Dashboard from './components/Dashboard';
import ProjectList from './components/ProjectList';
import ProjectForm from './components/ProjectForm';
import GanttView from './components/GanttView';
import Settings from './components/Settings';
import CalendarView from './components/CalendarView';
import TimeboxingView from './components/TimeboxingView';
import ArchivedTasksView from './components/ArchivedTasksView';
import DeleteProjectModal from './components/DeleteProjectModal';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ProtectedRoute from './components/ProtectedRoute';
import LogoutButton from './components/LogoutButton';
import SyncIndicator from './components/SyncIndicator';
import Toast from './components/Toast';
import OfflineBanner from './components/OfflineBanner';
import './App.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { PointsProvider } from './contexts/PointsContext';
import { TaskProvider } from './contexts/TaskContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { playTaskCompletionSound } from './utils/completionSound';
import DueTasksNotification from './components/DueTasksNotification';
import MissingSupabaseConfig from './components/MissingSupabaseConfig';
import { getSupabaseConfig } from './lib/supabaseClient';
import './i18n';
import { taskService } from './lib/supabase/services/taskService';
import { projectService } from './lib/supabase/services/projectService';
import { tagService } from './lib/supabase/services/tagService';
import { profileService } from './lib/supabase/services/profileService';
import { syncService } from './lib/supabase/services/syncService';
import { syncStatus } from './lib/supabase/utils/syncStatus';
import { getTasks, setTasks as setTasksLS, getProjects, setProjects as setProjectsLS, getTags, setTags as setTagsLS } from './lib/supabase/utils/localStorage';
import { useSyncOnFocus } from './lib/supabase/hooks/useSyncOnFocus';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { getStoredShortcuts, getShortcutsEnabled, shortcutToCombo } from './common/keyboardShortcutsStorage';
import { useOnlineStatus } from './lib/supabase/hooks/useOnlineStatus';
import { getAutoArchiveSettings, setAutoArchiveSettings } from './common/autoArchiveSettings';
import { getTaskTimeUnits } from './common/taskProgress';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [autoArchiveSettings, setAutoArchiveSettingsState] = useState(getAutoArchiveSettings);
  const autoArchiveRunRef = useRef('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const mainRef = useRef(null);

  // Load data from Supabase on mount and when user changes
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);

        // Fetch all data from Supabase
        const result = await syncService.fetchAllData();

        if (result.success && result.data) {
          const { tasks: remoteTasks, projects: remoteProjects, tags: remoteTags, profile } = result.data;

          // Merge remote and local data using syncService
          syncService.mergeWithLocalData(
            { tasks: remoteTasks, projects: remoteProjects, tags: remoteTags },
            { setTasks, setProjects, setTags }
          );

          if (profile?.autoArchiveEnabled !== undefined || profile?.autoArchiveDays !== undefined) {
            const storedSettings = setAutoArchiveSettings({
              enabled: profile.autoArchiveEnabled,
              days: profile.autoArchiveDays
            });
            setAutoArchiveSettingsState(storedSettings);
          }
        } else {
          // Fallback to localStorage if Supabase fetch fails
          console.warn('[AppContent] Failed to fetch from Supabase, using localStorage:', result.error);
          const localTasks = getTasks();
          const localProjects = getProjects();
          const localTags = getTags();

          setTasks(localTasks);
          setProjects(localProjects);
          setTags(localTags);
        }
      } catch (error) {
        console.error('[AppContent] Error loading data:', error);
        // Fallback to localStorage on error
        setTasks(getTasks());
        setProjects(getProjects());
        setTags(getTags());
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Save tasks, projects, and tags to localStorage whenever they change (cache layer)
  useEffect(() => {
    if (!isLoading && tasks.length >= 0) {
      setTasksLS(tasks);
    }
  }, [tasks, isLoading]);

  useEffect(() => {
    if (!isLoading && projects.length >= 0) {
      setProjectsLS(projects);
    }
  }, [projects, isLoading]);

  useEffect(() => {
    if (!isLoading && tags.length >= 0) {
      setTagsLS(tags);
    }
  }, [tags, isLoading]);

  // Reset main scroll to top on route change (synchronously to avoid visual jump)
  useLayoutEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  // Sync with Supabase when window regains focus
  useSyncOnFocus(async () => {
    if (!user) return;

    try {
      const result = await syncService.fetchAllData();

      if (result.success && result.data) {
        const { tasks: remoteTasks, projects: remoteProjects, tags: remoteTags } = result.data;

        // Merge with local data (last-write-wins)
        syncService.mergeWithLocalData(
          { tasks: remoteTasks, projects: remoteProjects, tags: remoteTags },
          { setTasks, setProjects, setTags }
        );
      }
    } catch (error) {
      console.error('[Sync] Error syncing on focus:', error);
    }
  }, { enabled: !isLoading });

  // Monitor online status and retry pending operations
  useOnlineStatus(
    { taskService, projectService, tagService },
    (status) => {

      // When back online, reload data to get latest changes
      if (status.isOnline && !status.hasPending && user) {
        syncService.fetchAllData().then((result) => {
          if (result.success && result.data) {
            const { tasks: remoteTasks, projects: remoteProjects, tags: remoteTags } = result.data;
            syncService.mergeWithLocalData(
              { tasks: remoteTasks, projects: remoteProjects, tags: remoteTags },
              { setTasks, setProjects, setTags }
            );
          }
        });
      }
    }
  );

  useEffect(() => {
    const handleAutoArchiveSettingsChange = (event) => {
      setAutoArchiveSettingsState(event.detail || getAutoArchiveSettings());
    };

    window.addEventListener('autoArchiveSettingsChanged', handleAutoArchiveSettingsChange);
    return () => {
      window.removeEventListener('autoArchiveSettingsChanged', handleAutoArchiveSettingsChange);
    };
  }, []);

  // Calculate points based on task priority and duration
  const calculateTaskPoints = (task) => {
    let basePoints = 10;
    const priorityMultiplier = task.priority === 'high' ? 1.5 : task.priority === 'medium' ? 1 : 0.7;
    let durationMultiplier = 1;

    if (task.isMultiDay && task.dueDate && task.endDate) {
      const startDate = new Date(task.dueDate);
      const endDate = new Date(task.endDate);
      const durationDays = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
      durationMultiplier = Math.min(3, 1 + (durationDays - 1) * 0.3);
    }

    return Math.round(basePoints * priorityMultiplier * durationMultiplier);
  };

  const applyTimeUnitScore = (task) => {
    const timeUnitScore = getTaskTimeUnits(task, new Date());
    const timeUnits = timeUnitScore?.tuScore || 0;
    return {
      ...task,
      timeUnits,
      timeUnitPoints: Math.trunc(timeUnits / 10)
    };
  };

  const addTask = async (task) => {
    const subDur = (task.subTasks || []).reduce((s, st) => s + (st.duration || 0), 0);

    // Generate temp ID and client request ID for idempotence
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newTask = {
      id: tempId,
      ...task,
      projectId: task.projectId || currentProjectId || 'default',
      createdAt: new Date().toISOString(),
      completed: false,
      pointsAwarded: false,
      pointsDeducted: false,
      subTasks: task.subTasks || [],
      tags: task.tags || [],
      isMultiDay: task.isMultiDay || false,
      flexMode: task.flexMode || false,
      antiOverlap: task.antiOverlap || false,
      duration: task.flexMode ? subDur : (task.duration || 60),
    };

    if (newTask.flexMode) {
      newTask.dueDate = null;
      newTask.startTime = null;
      newTask.endDate = null;
    }

    newTask.pointsValue = calculateTaskPoints(newTask);
    Object.assign(newTask, applyTimeUnitScore(newTask));

    // Optimistic update - add to UI immediately
    setTasks(prev => [...prev, newTask]);
    setShowTaskForm(false);

    // Sync with Supabase
    try {
      const result = await taskService.create(newTask, clientRequestId);

      if (result.success && result.data) {
        // Replace temp ID with real UUID from Supabase
        setTasks(prev => prev.map(t => t.id === tempId ? {
          ...result.data,
          pointsValue: newTask.pointsValue,
          timeUnits: newTask.timeUnits,
          timeUnitPoints: newTask.timeUnitPoints
        } : t));
        showToast({
          type: 'success',
          message: 'Task created successfully',
          duration: 3000
        });
      } else {
        // Mark as pending sync if failed
        console.warn('[addTask] Failed to sync with Supabase, marked as pending:', result.error);
        syncStatus.markPending(tempId, 'create', newTask, clientRequestId);
        showToast({
          type: 'warning',
          message: 'Task saved locally. Will sync when online.',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('[addTask] Error syncing task:', error);
      syncStatus.markPending(tempId, 'create', newTask, clientRequestId);
      showToast({
        type: 'error',
        message: 'Failed to sync task. Saved locally.',
        duration: 4000
      });
    }
  };

  const updateTask = async (updatedTask) => {
    if (!updatedTask.isMultiDay) {
      delete updatedTask.endDate;
    }

    if (updatedTask.flexMode) {
      updatedTask.dueDate = null;
      updatedTask.startTime = null;
      updatedTask.endDate = null;
      updatedTask.isMultiDay = false;
      updatedTask.duration = (updatedTask.subTasks || []).reduce((s, st) => s + (st.duration || 0), 0);
    } else {
      updatedTask.subTasks = (updatedTask.subTasks || []).map(st => ({
        ...st,
        dueDate: st.dueDate || null,
        time: st.time || null,
        endDate: st.endDate || null
      }));
    }

    updatedTask.pointsValue = calculateTaskPoints(updatedTask);
    Object.assign(updatedTask, applyTimeUnitScore(updatedTask));

    // Optimistic update - update UI immediately
    setTasks(prev => prev.map(task => {
      if (task.id === updatedTask.id) {
        if (task.completed === updatedTask.completed) {
          return {
            ...updatedTask,
            pointsAwarded: task.pointsAwarded,
            pointsDeducted: task.pointsDeducted
          };
        }
        return {
          ...updatedTask,
          pointsAwarded: updatedTask.completed ? true : false,
          pointsDeducted: false
        };
      }
      return task;
    }));

    setEditingTask(null);
    setShowTaskForm(false);

    // Sync with Supabase
    const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      const result = await taskService.update(updatedTask.id, updatedTask, clientRequestId);

      if (result.success && result.data) {
        // Update with Supabase data (preserves points flags)
        setTasks(prev => prev.map(task => {
          if (task.id === updatedTask.id) {
            return {
              ...result.data,
              pointsAwarded: task.pointsAwarded,
              pointsDeducted: task.pointsDeducted,
              pointsValue: updatedTask.pointsValue,
              timeUnits: updatedTask.timeUnits,
              timeUnitPoints: updatedTask.timeUnitPoints
            };
          }
          return task;
        }));
      } else {
        // Mark as pending sync if failed (only if not temp ID)
        if (!syncStatus.isTempId(updatedTask.id)) {
          console.warn('[updateTask] Failed to sync with Supabase, marked as pending:', result.error);
          syncStatus.markPending(updatedTask.id, 'update', updatedTask, clientRequestId);
          showToast({
            type: 'warning',
            message: 'Update saved locally. Will sync when online.',
            duration: 4000
          });
        }
      }
    } catch (error) {
      console.error('[updateTask] Error syncing task:', error);
      if (!syncStatus.isTempId(updatedTask.id)) {
        syncStatus.markPending(updatedTask.id, 'update', updatedTask, clientRequestId);
        showToast({
          type: 'error',
          message: 'Failed to sync update. Saved locally.',
          duration: 4000
        });
      }
    }
  };

  const deleteTask = async (taskId) => {
    // Optimistic update - remove from UI immediately
    setTasks(prev => {
      const updatedTasks = prev.filter(task => task.id !== taskId);
      return updatedTasks.filter(task => task.parentTaskId !== taskId);
    });

    // Sync with Supabase (soft delete)
    const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      const result = await taskService.delete(taskId, clientRequestId);

      if (!result.success) {
        // If delete failed, restore the task (revert optimistic update)
        console.warn('[deleteTask] Failed to sync delete with Supabase:', result.error);

        // Only mark as pending if not a temp ID (temp tasks haven't been created yet)
        if (!syncStatus.isTempId(taskId)) {
          // Note: For delete, we can't easily restore without fetching from localStorage
          // This is acceptable - the task will be removed locally but may reappear on next sync
          syncStatus.markPending(taskId, 'delete', { id: taskId }, clientRequestId);
          showToast({
            type: 'warning',
            message: 'Delete will sync when online',
            duration: 3000
          });
        }
      } else {
        showToast({
          type: 'success',
          message: 'Task deleted',
          duration: 2000
        });
      }
    } catch (error) {
      console.error('[deleteTask] Error syncing delete:', error);
      if (!syncStatus.isTempId(taskId)) {
        syncStatus.markPending(taskId, 'delete', { id: taskId }, clientRequestId);
        showToast({
          type: 'error',
          message: 'Failed to sync delete. Will retry later.',
          duration: 4000
        });
      }
    }
  };

  const archiveTask = async (taskId) => {
    const archivedAt = new Date().toISOString();
    const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const currentTask = tasks.find(task => task.id === taskId);
    let pendingArchiveTask = currentTask
      ? { ...currentTask, archived: true, archivedAt }
      : null;

    // Optimistic update - update UI immediately
    setTasks(prev => prev.map(task => {
      if (task.id === taskId) {
        pendingArchiveTask = {
          ...task,
          archived: true,
          archivedAt
        };
        return pendingArchiveTask;
      }
      return task;
    }));

    // Sync with Supabase
    try {
      const result = await taskService.archiveTask(taskId);

      if (result.success && result.data) {
        setTasks(prev => prev.map(task =>
          task.id === taskId ? { ...task, ...result.data } : task
        ));
        showToast({
          type: 'success',
          message: 'Task archived',
          duration: 2000
        });
      } else {
        if (!syncStatus.isTempId(taskId) && pendingArchiveTask) {
          syncStatus.markPending(taskId, 'update', pendingArchiveTask, clientRequestId);
        }
        showToast({
          type: 'warning',
          message: 'Task archived locally. Sync will retry.',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('[archiveTask] Error:', error);
      if (!syncStatus.isTempId(taskId) && pendingArchiveTask) {
        syncStatus.markPending(taskId, 'update', pendingArchiveTask, clientRequestId);
      }
      showToast({
        type: 'warning',
        message: 'Task archived locally. Sync will retry.',
        duration: 4000
      });
    }
  };

  const unarchiveTask = async (taskId) => {
    const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const currentTask = tasks.find(task => task.id === taskId);
    let pendingRestoreTask = currentTask
      ? { ...currentTask, archived: false, archivedAt: null }
      : null;

    // Optimistic update - update UI immediately
    setTasks(prev => prev.map(task => {
      if (task.id === taskId) {
        pendingRestoreTask = {
          ...task,
          archived: false,
          archivedAt: null
        };
        return pendingRestoreTask;
      }
      return task;
    }));

    // Sync with Supabase
    try {
      const result = await taskService.unarchiveTask(taskId);

      if (result.success && result.data) {
        setTasks(prev => prev.map(task =>
          task.id === taskId ? { ...task, ...result.data } : task
        ));
        showToast({
          type: 'success',
          message: 'Task restored',
          duration: 2000
        });
      } else {
        if (!syncStatus.isTempId(taskId) && pendingRestoreTask) {
          syncStatus.markPending(taskId, 'update', pendingRestoreTask, clientRequestId);
        }
        showToast({
          type: 'warning',
          message: 'Task restored locally. Sync will retry.',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('[unarchiveTask] Error:', error);
      if (!syncStatus.isTempId(taskId) && pendingRestoreTask) {
        syncStatus.markPending(taskId, 'update', pendingRestoreTask, clientRequestId);
      }
      showToast({
        type: 'warning',
        message: 'Task restored locally. Sync will retry.',
        duration: 4000
      });
    }
  };

  useEffect(() => {
    if (isLoading || !autoArchiveSettings.enabled) return;

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - autoArchiveSettings.days);

    const candidates = tasks.filter(task => (
      task.completed &&
      !task.archived &&
      task.completedAt &&
      new Date(task.completedAt) < cutoff
    ));

    if (candidates.length === 0) return;

    const runKey = `${autoArchiveSettings.days}:${candidates.map(task => task.id).sort().join(',')}`;
    if (autoArchiveRunRef.current === runKey) return;
    autoArchiveRunRef.current = runKey;

    const runAutoArchive = async () => {
      for (const task of candidates) {
        await archiveTask(task.id);
      }

      showToast({
        type: 'success',
        message: `${candidates.length} completed task${candidates.length > 1 ? 's' : ''} auto-archived`,
        duration: 4000
      });
    };

    runAutoArchive();
  }, [autoArchiveSettings, isLoading, tasks]);

  const updateAutoArchiveSettings = async (settings) => {
    const normalized = setAutoArchiveSettings(settings);
    setAutoArchiveSettingsState(normalized);

    if (user) {
      try {
        const result = await profileService.updateSettings({
          autoArchiveEnabled: normalized.enabled,
          autoArchiveDays: normalized.days
        });

        if (!result.success) {
          console.warn('[autoArchive] Failed to sync settings:', result.error);
        }
      } catch (error) {
        console.error('[autoArchive] Error syncing settings:', error);
      }
    }
  };

  const toggleTaskComplete = async (taskId) => {
    let updatedTaskForSync = null;

    // Optimistic update - update UI immediately
    setTasks(prev => prev.map(task => {
      if (task.id === taskId) {
        const nowCompleted = !task.completed;
        const points = calculateTaskPoints(task);
        const shouldAwardPoints = nowCompleted && !task.pointsAwarded;
        const shouldRemovePoints = !nowCompleted && task.pointsAwarded;

        if (nowCompleted) {
          playTaskCompletionSound('main');
        }

        const nowIso = new Date().toISOString();
        const updatedSubs = (task.subTasks || []).map(st => ({
          ...st,
          completed: nowCompleted,
          completedAt: nowCompleted ? (st.completedAt || nowIso) : null
        }));

        const updated = {
          ...task,
          subTasks: updatedSubs,
          completed: nowCompleted,
          completedAt: nowCompleted ? nowIso : null,
          pointsAwarded: shouldAwardPoints ? true : shouldRemovePoints ? false : task.pointsAwarded,
          pointsValue: points
        };

        updatedTaskForSync = applyTimeUnitScore(updated);
        return updatedTaskForSync;
      }

      const subIndex = task.subTasks ? task.subTasks.findIndex(st => st.id === taskId) : -1;
      if (subIndex !== -1) {
        const updatedSub = {
          ...task.subTasks[subIndex],
          completed: !task.subTasks[subIndex].completed,
          completedAt: !task.subTasks[subIndex].completed ? new Date().toISOString() : null
        };

        if (updatedSub.completed) {
          playTaskCompletionSound('sub');
        }

        const updatedSubs = task.subTasks.map((st, i) => (i === subIndex ? updatedSub : st));
        const allDone = updatedSubs.every(st => st.completed);
        const points = calculateTaskPoints(task);
        const shouldAwardPoints = allDone && !task.pointsAwarded;
        const shouldRemovePoints = !allDone && task.pointsAwarded;

        const updated = {
          ...task,
          subTasks: updatedSubs,
          completed: allDone,
          completedAt: allDone ? new Date().toISOString() : null,
          pointsAwarded: shouldAwardPoints ? true : shouldRemovePoints ? false : task.pointsAwarded,
          pointsValue: points
        };

        updatedTaskForSync = applyTimeUnitScore(updated);
        return updatedTaskForSync;
      }

      return task;
    }));

    // Sync with Supabase
    if (updatedTaskForSync) {
      const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      try {
        const result = await taskService.update(updatedTaskForSync.id, updatedTaskForSync);

        if (result.success && result.data) {
          // Update with Supabase data (preserves points flags)
          setTasks(prev => prev.map(task => {
            if (task.id === updatedTaskForSync.id) {
              return {
                ...result.data,
                pointsAwarded: task.pointsAwarded,
                pointsDeducted: task.pointsDeducted,
                pointsValue: updatedTaskForSync.pointsValue,
                timeUnits: updatedTaskForSync.timeUnits,
                timeUnitPoints: updatedTaskForSync.timeUnitPoints
              };
            }
            return task;
          }));
        } else {
          // Mark as pending sync if failed (only if not temp ID)
          if (!syncStatus.isTempId(updatedTaskForSync.id)) {
            console.warn('[toggleTaskComplete] Failed to sync with Supabase, marked as pending:', result.error);
            syncStatus.markPending(updatedTaskForSync.id, 'update', updatedTaskForSync, clientRequestId);
          }
        }
      } catch (error) {
        console.error('[toggleTaskComplete] Error syncing task:', error);
        if (!syncStatus.isTempId(updatedTaskForSync.id)) {
          syncStatus.markPending(updatedTaskForSync.id, 'update', updatedTaskForSync, clientRequestId);
        }
      }
    }
  };

  const addProject = async (project) => {
    // Generate temp ID and client request ID for idempotence
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newProject = {
      id: tempId,
      ...project,
      createdAt: new Date().toISOString()
    };

    // Optimistic update - add to UI immediately
    setProjects(prev => [...prev, newProject]);
    setShowProjectForm(false);

    // Sync with Supabase
    try {
      const result = await projectService.create(newProject, clientRequestId);

      if (result.success && result.data) {
        // Replace temp ID with real UUID from Supabase
        setProjects(prev => prev.map(p => p.id === tempId ? result.data : p));

        // Update any tasks that reference the temp project ID
        setTasks(prev => prev.map(t => t.projectId === tempId ? { ...t, projectId: result.data.id } : t));
      } else {
        // Mark as pending sync if failed
        console.warn('[addProject] Failed to sync with Supabase, marked as pending:', result.error);
        syncStatus.markPending(tempId, 'create', newProject, clientRequestId);
      }
    } catch (error) {
      console.error('[addProject] Error syncing project:', error);
      syncStatus.markPending(tempId, 'create', newProject, clientRequestId);
    }
  };

  const updateProject = async (updatedProject) => {
    if (updatedProject.id === 'default') return;

    // Optimistic update - update UI immediately
    setProjects(prev => prev.map(project =>
      project.id === updatedProject.id ? updatedProject : project
    ));
    setEditingProject(null);
    setShowProjectForm(false);

    // Sync with Supabase
    const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      const result = await projectService.update(updatedProject.id, updatedProject, clientRequestId);

      if (result.success && result.data) {
        // Update with Supabase data
        setProjects(prev => prev.map(project =>
          project.id === updatedProject.id ? result.data : project
        ));
      } else {
        // Mark as pending sync if failed (only if not temp ID)
        if (!syncStatus.isTempId(updatedProject.id)) {
          console.warn('[updateProject] Failed to sync with Supabase, marked as pending:', result.error);
          syncStatus.markPending(updatedProject.id, 'update', updatedProject, clientRequestId);
        }
      }
    } catch (error) {
      console.error('[updateProject] Error syncing project:', error);
      if (!syncStatus.isTempId(updatedProject.id)) {
        syncStatus.markPending(updatedProject.id, 'update', updatedProject, clientRequestId);
      }
    }
  };

  const initiateProjectDelete = (projectId) => {
    if (projectId === 'default') return;
    setProjectToDelete(projectId);
    setShowDeleteProjectModal(true);
  };

  const deleteProject = async (projectId, action, targetProjectId) => {
    if (projectId === 'default') return;

    // Optimistic update - update UI immediately
    if (action === 'move' && targetProjectId) {
      setTasks(prev => prev.map(task =>
        task.projectId === projectId ? { ...task, projectId: targetProjectId } : task
      ));
    } else if (action === 'inbox') {
      setTasks(prev => prev.map(task =>
        task.projectId === projectId ? { ...task, projectId: 'default' } : task
      ));
    } else if (action === 'delete') {
      setTasks(prev => prev.filter(task => task.projectId !== projectId));
    }

    setProjects(prev => prev.filter(project => project.id !== projectId));

    if (currentProjectId === projectId) {
      setCurrentProjectId(null);
    }

    setShowDeleteProjectModal(false);

    // Sync with Supabase (soft delete)
    const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      const result = await projectService.delete(projectId, clientRequestId);

      if (!result.success) {
        console.warn('[deleteProject] Failed to sync delete with Supabase:', result.error);

        // Only mark as pending if not a temp ID
        if (!syncStatus.isTempId(projectId)) {
          syncStatus.markPending(projectId, 'delete', { id: projectId }, clientRequestId);
        }
      }
    } catch (error) {
      console.error('[deleteProject] Error syncing delete:', error);
      if (!syncStatus.isTempId(projectId)) {
        syncStatus.markPending(projectId, 'delete', { id: projectId }, clientRequestId);
      }
    }
  };

  const handleEditTask = (task, subtaskId = null) => {
    setEditingTask(task);
    setEditingSubtaskId(subtaskId);
    setShowTaskForm(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowProjectForm(true);
  };

  const addTag = async (tag) => {
    // Check if tag already exists (case-insensitive)
    const existingTag = tags.find(t => t.name.toLowerCase() === tag.name.toLowerCase());
    if (existingTag) {
      return existingTag;
    }

    // Generate temp ID and client request ID for idempotence
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newTag = {
      id: tempId,
      ...tag
    };

    // Optimistic update - add to UI immediately
    setTags(prev => [...prev, newTag]);

    // Sync with Supabase
    try {
      const result = await tagService.create(newTag, clientRequestId);

      if (result.success && result.data) {
        // Replace temp ID with real UUID from Supabase
        const realId = result.data.id;
        setTags(prev => prev.map(t => t.id === tempId ? result.data : t));

        // Update any tasks that reference the temp tag ID
        setTasks(prev => prev.map(task => ({
          ...task,
          tags: task.tags ? task.tags.map(tid => tid === tempId ? realId : tid) : []
        })));

        return result.data;
      } else {
        // Mark as pending sync if failed
        console.warn('[addTag] Failed to sync with Supabase, marked as pending:', result.error);
        syncStatus.markPending(tempId, 'create', newTag, clientRequestId);
        return newTag;
      }
    } catch (error) {
      console.error('[addTag] Error syncing tag:', error);
      syncStatus.markPending(tempId, 'create', newTag, clientRequestId);
      return newTag;
    }
  };

  const removeTag = async (tagId) => {
    // Optimistic update - remove from UI immediately
    setTasks(prev => prev.map(task => ({
      ...task,
      tags: task.tags ? task.tags.filter(id => id !== tagId) : []
    })));
    setTags(prev => prev.filter(tag => tag.id !== tagId));
    setSelectedTags(prev => prev.filter(id => id !== tagId));

    // Sync with Supabase (soft delete)
    const clientRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      const result = await tagService.delete(tagId, clientRequestId);

      if (!result.success) {
        console.warn('[removeTag] Failed to sync delete with Supabase:', result.error);

        // Only mark as pending if not a temp ID
        if (!syncStatus.isTempId(tagId)) {
          syncStatus.markPending(tagId, 'delete', { id: tagId }, clientRequestId);
        }
      } else {
        showToast({
          type: 'success',
          message: 'Tag deleted',
          duration: 2000
        });
      }
    } catch (error) {
      console.error('[removeTag] Error syncing delete:', error);
      if (!syncStatus.isTempId(tagId)) {
        syncStatus.markPending(tagId, 'delete', { id: tagId }, clientRequestId);
      }
    }
  };

  const updateTaskDates = (taskId, newStartDate, newEndDate) => {
    setTasks(prev => prev.map(task => {
      if (task.id === taskId) {
        if (!newStartDate) {
          const updated = {
            ...task,
            dueDate: null,
            startTime: null,
            endDate: null,
            isMultiDay: false
          };
          updated.pointsValue = calculateTaskPoints(updated);
          return updated;
        }

        const startStr = `${newStartDate
          .getHours()
          .toString()
          .padStart(2, '0')}:${newStartDate
          .getMinutes()
          .toString()
          .padStart(2, '0')}`;

        const isMultiDay = newEndDate && newStartDate.toDateString() !== newEndDate.toDateString();
        const toDateString = (d) => d.toISOString().split('T')[0];

        const updated = {
          ...task,
          dueDate: toDateString(newStartDate),
          startTime: startStr,
          endDate: isMultiDay ? toDateString(newEndDate) : null,
          duration: (newEndDate.getTime() - newStartDate.getTime()) / 60000,
          isMultiDay
        };
        updated.pointsValue = calculateTaskPoints(updated);
        return updated;
      }

      const subIndex = task.subTasks ? task.subTasks.findIndex(st => st.id === taskId) : -1;
      if (subIndex !== -1) {
        const existing = task.subTasks[subIndex];
        let updatedSub;

        if (!newStartDate) {
          updatedSub = {
            ...existing,
            dueDate: null,
            time: null,
            duration: null,
            endDate: null
          };
        } else {
          let start = new Date(newStartDate);
          let end = new Date(newEndDate);

          if (!task.flexMode) {
            const parentStart = new Date(task.dueDate);
            if (task.startTime) {
              const [ph, pm] = task.startTime.split(':').map(Number);
              parentStart.setHours(ph, pm, 0, 0);
            }

            const parentEnd = task.isMultiDay
              ? (task.endDate ? new Date(task.endDate) : parentStart)
              : new Date(parentStart.getTime() + (task.duration || 0) * 60000);

            if (start < parentStart) start = parentStart;
            if (end > parentEnd) end = parentEnd;
            if (end < start) end = new Date(start);
          }

          const toDateString = (d) => d.toISOString().split('T')[0];

          if (existing.isMultiDay) {
            updatedSub = {
              ...existing,
              dueDate: toDateString(start),
              endDate: toDateString(end),
              time: null,
              duration: (end.getTime() - start.getTime()) / 60000
            };
          } else {
            const startStr = `${start
              .getHours()
              .toString()
              .padStart(2, '0')}:${start
              .getMinutes()
              .toString()
              .padStart(2, '0')}`;

            updatedSub = {
              ...existing,
              dueDate: toDateString(start),
              time: startStr,
              duration: (end.getTime() - start.getTime()) / 60000,
              endDate: null
            };
          }
        }

        const updatedTask = {
          ...task,
          subTasks: task.subTasks.map((st, i) => (i === subIndex ? updatedSub : st))
        };

        if (task.flexMode) {
          updatedTask.duration = updatedTask.subTasks.reduce((s, st) => s + (st.duration || 0), 0);
        }

        updatedTask.pointsValue = calculateTaskPoints(updatedTask);
        return updatedTask;
      }

      return task;
    }));
  };

  // Filter out archived tasks for the main views
  const activeTasks = tasks.filter(task => !task.archived);

  // Get archived tasks
  const archivedTasks = tasks.filter(task => task.archived);

  const filteredTasks = activeTasks.filter(task => {
    const matchesProject = currentProjectId ? task.projectId === currentProjectId : true;

    const matchesFilter =
      filter === 'all' ||
      (filter === 'completed' && task.completed) ||
      (filter === 'pending' && !task.completed) ||
      (filter === 'high' && task.priority === 'high') ||
      (filter === 'medium' && task.priority === 'medium') ||
      (filter === 'low' && task.priority === 'low');

    const searchParts = searchQuery
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    const searchableContent = [
      task.title,
      task.description,
      ...(task.tags || []).map(tagId => {
        const tag = tags.find(t => t.id === tagId);
        return tag ? tag.name : '';
      })
    ]
      .join(' ')
      .toLowerCase();

    const matchesSearch = searchParts.every(part => searchableContent.includes(part));

    const matchesTags =
      selectedTags.length === 0 ||
      (task.tags && selectedTags.every(tagId => task.tags.includes(tagId)));

    return matchesProject && matchesFilter && matchesSearch && matchesTags;
  });

  const taskContextValue = {
    tasks: activeTasks,
    filteredTasks,
    archivedTasks,
    addTask,
    updateTask,
    deleteTask,
    toggleTaskComplete,
    archiveTask,
    unarchiveTask,
    handleEditTask,
    updateTaskDates,
    selectedTags,
    setSelectedTags
  };

  const projectContextValue = {
    projects,
    currentProjectId,
    setCurrentProjectId,
    addProject,
    updateProject,
    initiateProjectDelete,
    deleteProject,
    handleEditProject
  };

  // Keyboard shortcuts (customizable, stored in localStorage)
  const shortcutsConfig = getStoredShortcuts();
  const shortcutsEnabled = getShortcutsEnabled();

  const dynamicShortcuts = {};

  const addShortcut = (configKey, handler) => {
    const cfg = shortcutsConfig[configKey];
    if (!cfg) return;
    const combo = shortcutToCombo(cfg);
    // Support both ctrl and cmd for cross-platform compatibility
    if (cfg.modifiers?.includes('ctrl') || cfg.modifiers?.includes('cmd')) {
      dynamicShortcuts[combo] = handler;
      const altCombo = combo.replace('ctrl+', 'cmd+').replace('cmd+', 'ctrl+');
      if (altCombo !== combo) {
        dynamicShortcuts[altCombo] = handler;
      }
    } else {
      dynamicShortcuts[combo] = handler;
    }
  };

  addShortcut('newTask', () => {
    setEditingTask(null);
    setShowTaskForm(true);
  });
  addShortcut('newProject', () => {
    setEditingProject(null);
    setShowProjectForm(true);
  });
  addShortcut('focusSearch', () => {
    const searchInput = document.querySelector('input[type="text"][placeholder]');
    searchInput?.focus();
  });
  addShortcut('goDashboard', () => navigate('/'));
  addShortcut('goTasks', () => navigate('/tasks'));
  addShortcut('goProjects', () => navigate('/projects'));
  addShortcut('goGantt', () => navigate('/gantt'));
  addShortcut('goCalendar', () => navigate('/calendar'));
  addShortcut('goTimeboxing', () => navigate('/timeboxing'));
  addShortcut('goArchives', () => navigate('/archives'));
  addShortcut('goSettings', () => navigate('/settings'));

  dynamicShortcuts['esc'] = () => {
    if (showTaskForm) {
      setShowTaskForm(false);
      setEditingTask(null);
      setEditingSubtaskId(null);
    } else if (showProjectForm) {
      setShowProjectForm(false);
      setEditingProject(null);
    } else if (showDeleteProjectModal) {
      setShowDeleteProjectModal(false);
    }
  };

  useKeyboardShortcuts(dynamicShortcuts, { enabled: shortcutsEnabled && !isLoading });

  // Show loading state while initializing
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 dark:text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement de vos données...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <PointsProvider
        tasks={tasks}
      >
        <ProjectProvider value={projectContextValue}>
          <TaskProvider value={taskContextValue}>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 dark:text-white transition-colors duration-300">
              <OfflineBanner />
              <div className="flex min-h-screen">
                <Sidebar
                  filter={filter}
                  setFilter={setFilter}
                  onAddTask={() => {
                    setEditingTask(null);
                    setShowTaskForm(true);
                  }}
                  onAddProject={() => {
                    setEditingProject(null);
                    setShowProjectForm(true);
                  }}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  projects={projects}
                  currentProjectId={currentProjectId}
                  setCurrentProjectId={setCurrentProjectId}
                  tasksCount={{
                    total: filteredTasks.length,
                    completed: filteredTasks.filter(t => t.completed).length,
                    pending: filteredTasks.filter(t => !t.completed).length
                  }}
                  tags={tags}
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                  onDeleteTag={removeTag}
                  mobileOpen={mobileSidebarOpen}
                  onMobileClose={() => setMobileSidebarOpen(false)}
                />

                <main ref={mainRef} className="flex-1 overflow-y-auto min-h-screen p-4 md:p-6">
                  {/* Mobile Header */}
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setMobileSidebarOpen(true)}
                        className="md:hidden p-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
                        aria-label="Menu"
                        type="button"
                      >
                        <FiIcons.FiMenu className="text-xl" />
                      </button>
                      <AnimatePresence>
                        {location.pathname === '/' && (
                          <motion.img
                            key="header-logo"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.2 }}
                            src="/logos/logo.png"
                            alt="Efficacitas"
                            className="h-[126px] w-auto object-contain"
                          />
                        )}
                      </AnimatePresence>
                    </div>
                    <div className="flex-1" />
                    {/* Sync & Logout only on Dashboard */}
                    <AnimatePresence>
                      {location.pathname === '/' && (
                        <motion.div
                          key="header-actions"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                          className="flex items-center gap-3"
                        >
                          <SyncIndicator onRetry={async () => {
                            // Retry pending sync operations
                            try {
                              const result = await syncService.fetchAllData();
                              if (result.success && result.data) {
                                const { tasks: remoteTasks, projects: remoteProjects, tags: remoteTags } = result.data;
                                syncService.mergeWithLocalData(
                                  { tasks: remoteTasks, projects: remoteProjects, tags: remoteTags },
                                  { setTasks, setProjects, setTags }
                                );
                              }
                            } catch (error) {
                              console.error('[Retry] Failed to sync:', error);
                            }
                          }} />
                          <LogoutButton />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <AnimatePresence mode="popLayout">
                    <motion.div
                      key={location.pathname}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Routes location={location}>
                    <Route
                      path="/"
                      element={
                        <Dashboard
                          tasks={tasks}
                          projects={projects}
                          onAddTask={() => {
                            setEditingTask(null);
                            setShowTaskForm(true);
                          }}
                          onAddProject={() => {
                            setEditingProject(null);
                            setShowProjectForm(true);
                          }}
                          currentProjectId={currentProjectId}
                          setFilter={setFilter}
                          setCurrentProjectId={setCurrentProjectId}
                        />
                      }
                    />
                    <Route
                      path="/tasks"
                      element={
                        <TaskList
                          tasks={filteredTasks}
                          projects={projects}
                          onToggleComplete={toggleTaskComplete}
                          onEditTask={handleEditTask}
                          onDeleteTask={deleteTask}
                          onArchiveTask={archiveTask}
                          filter={filter}
                          tags={tags}
                          isLoading={isLoading}
                        />
                      }
                    />
                    <Route
                      path="/projects"
                      element={
                        <ProjectList
                          projects={projects}
                          tasks={tasks}
                          onEditProject={handleEditProject}
                          onDeleteProject={initiateProjectDelete}
                          onAddProject={() => {
                            setEditingProject(null);
                            setShowProjectForm(true);
                          }}
                          onSelectProject={(projectId) => {
                            setCurrentProjectId(projectId);
                            navigate('/tasks');
                          }}
                          isLoading={isLoading}
                        />
                      }
                    />
                    <Route
                      path="/gantt"
                      element={
                        <GanttView
                          tasks={tasks}
                          projects={projects}
                          currentProjectId={currentProjectId}
                          onTaskUpdate={updateTask}
                          updateTaskDates={updateTaskDates}
                          onToggleComplete={toggleTaskComplete}
                          searchQuery={searchQuery}
                          tags={tags}
                        />
                      }
                    />
                    <Route
                      path="/calendar"
                      element={
                        <CalendarView
                          tasks={tasks}
                          projects={projects}
                          currentProjectId={currentProjectId}
                          onTaskEdit={handleEditTask}
                          onTaskUpdate={updateTask}
                          updateTaskDates={updateTaskDates}
                          onToggleComplete={toggleTaskComplete}
                          searchQuery={searchQuery}
                          tags={tags}
                        />
                      }
                    />
                    <Route
                      path="/timeboxing"
                      element={
                        <TimeboxingView
                          tasks={tasks}
                          projects={projects}
                          currentProjectId={currentProjectId}
                          onTaskEdit={handleEditTask}
                          updateTaskDates={updateTaskDates}
                          onToggleComplete={toggleTaskComplete}
                          searchQuery={searchQuery}
                          tags={tags}
                        />
                      }
                    />
                    <Route
                      path="/archives"
                      element={
                        <ArchivedTasksView
                          tasks={archivedTasks}
                          projects={projects}
                          onUnarchive={unarchiveTask}
                          onDelete={deleteTask}
                          tags={tags}
                          isLoading={isLoading}
                        />
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <Settings
                          tasks={tasks}
                          projects={projects}
                          tags={tags}
                          setTasks={setTasks}
                          setProjects={setProjects}
                          setTags={setTags}
                          autoArchiveSettings={autoArchiveSettings}
                          onAutoArchiveSettingsChange={updateAutoArchiveSettings}
                        />
                      }
                    />
                  </Routes>
                    </motion.div>
                  </AnimatePresence>
                </main>
              </div>

              <AnimatePresence>
                {showTaskForm && (
                  <TaskForm
                    task={editingTask}
                    tasks={tasks}
                    editingSubtaskId={editingSubtaskId}
                    projects={projects}
                    currentProjectId={currentProjectId}
                    onSubmit={editingTask ? updateTask : addTask}
                    onClose={() => {
                      setShowTaskForm(false);
                      setEditingTask(null);
                      setEditingSubtaskId(null);
                    }}
                    tags={tags}
                    onAddTag={addTag}
                  />
                )}
              </AnimatePresence>

              <AnimatePresence>
                {showProjectForm && (
                  <ProjectForm
                    project={editingProject}
                    onSubmit={editingProject ? updateProject : addProject}
                    onClose={() => {
                      setShowProjectForm(false);
                      setEditingProject(null);
                    }}
                  />
                )}
              </AnimatePresence>

              <AnimatePresence>
                {showDeleteProjectModal && (
                  <DeleteProjectModal
                    projectId={projectToDelete}
                    projects={projects.filter(p => p.id !== projectToDelete && p.id !== 'default')}
                    onClose={() => setShowDeleteProjectModal(false)}
                    onConfirm={deleteProject}
                  />
                )}
              </AnimatePresence>

              <DueTasksNotification
                tasks={tasks}
                setCurrentProjectId={setCurrentProjectId}
                setFilter={setFilter}
              />
            </div>
          </TaskProvider>
        </ProjectProvider>
      </PointsProvider>
    </ThemeProvider>
  );
}

function App() {
  const supabaseConfig = getSupabaseConfig();

  if (!supabaseConfig.ok) {
    return <MissingSupabaseConfig config={supabaseConfig} />;
  }

  return (
    <ToastProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AppContent />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
        <Toast />
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
